import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import * as Yup from "yup";
import api from "../../services/api";

import { Toast } from "../../shared/Toast";
import { languageOptions, LocalPricePackages, pricePackages } from "../../utils/constants";

const validationSchema = Yup.object({
  package_type: Yup.string().min(1).required("Package Type is required"),
  lang_ids: Yup.array().min(1, 'Language is required!').required("Language is required"),
  state_ids: Yup.array().required("State is required"),
  district_ids: Yup.array().required("District is required"),
  constituency_ids: Yup.array().required("Constituency is required"),
  package_weeks: Yup.number().required('Package weeks is required'),
  creative_type: Yup.string().required("Creative type is required"),
  position: Yup.number().when('package_type', {
    is: 'Alpha',
    then: (schema) => schema.required('Position is required!'),
    otherwise: (schema) => schema.notRequired().nullable().transform((value, originalValue) => {
      // Transform empty string to null for non-Alpha packages
      return originalValue === '' ? 0 : value;
    })
  })
});

function PackageOrder({ packageValue, setPackageValue, setPackageDetails, packageDetails, disable, salesType, updatedPackageValue, setUpdatedPackageValue }) {
  const [constituencyOptions, setConstituencyOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [formChanged, setFormChanged] = useState(false)
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      package_type: "",
      lang_ids: [],
      state_ids: [],
      district_ids: [],
      constituency_ids: [],
      creative_type: "",
      position: "",
      package_weeks: "",
    },mode:'all'
  });
  console.log(salesType, 'salesType')
  // Watch form values for dependent fields
  const watchedValues = watch();
  function percentChange(a, b) {
    if (a === 0) {
      if (b === 0) return 0; // no change
      return b > 0 ? Infinity : -Infinity; // b compared to 0
    }
    return ((b - a) / a) * 100;
  }

  const positionOptions = [
    { label: "1", value: "1" },
    { label: "3", value: "3" },
    { label: "5", value: "5" },
    { label: "9", value: "9" },
    { label: "12", value: "12" },
    { label: "15", value: "15" },
    { label: "19", value: "19" },
    { label: "23", value: "23" },
    { label: "27", value: "27" },
  ];
  const handleFormSubmissionWithReset = async (fieldName, newValue) => {
    if (isInitialLoad) return;
    
    // Reset package value immediately when any field changes
    setPackageValue(0);
    setTimeout(async () => {
      // Get current form values
      const currentValues = watch();
      
      // Check if all required fields are filled
      const hasRequiredFields = 
        currentValues.package_type && 
        currentValues.lang_ids?.length > 0 && 
        currentValues.state_ids?.length > 0 && 
        currentValues.package_weeks && 
        currentValues.creative_type &&
        (currentValues.package_type !== 'Alpha' || currentValues.position);
      
      if (hasRequiredFields) {
        console.log("All required fields present, submitting...");
        handleSubmit(onSubmit)();
      } else {
        console.log("Missing required fields, package value remains 0");
        // Package value is already 0 from the reset above
      }
    }, 1000);
  };

  const onSubmit = async (data) => {
    if (!data.package_type || !data.lang_ids || !data.state_ids || !data.package_weeks || !data.creative_type) {
      setPackageValue(0);
      return;
    }
    try {
    setFormChanged(true)
      const res = await api({
        url: '/packagerates/getpackagevalue',
        method: 'POST',
        responseType: 'json',
        data: {
          ...data
        }
      });
      
      if (res.code === 200) {
        setPackageValue(res.data);
        if(formChanged)
        setUpdatedPackageValue(res.data)
        if (res.data === 0) {
          Toast('error', "No data found");
          return;
        }
        setPackageDetails({...data,salesType});
      }
    } catch (error) {
      console.error(error);
      Toast('error', "Error while fetching prices");
    }
  };

  const lang_idsOnchange = async (selected, edit = false) => {
    let langs = [];
    if(selected.length > 0){  
      if (typeof selected[0] === 'object') {
        // If selected is an array of objects, extract the state_id from each object
        langs = selected.map(item => item.value);
      } else {
        // If selected is an array of integers, use them directly
        langs = selected;
      }
    }
    
    try {
      const res = await api({
        url: '/location/getStatesByLangId',
        method: 'POST',
        responseType: 'json',
        data: langs
      });
      
      const states = [];
      for (let item of res.data) {
        const temp = { value: item, label: `${item.state_name}` };
        states.push(temp);
      }
      setStateOptions(states);
      
      if (!edit && !isInitialLoad) {
        setValue("state_ids", watchedValues.state_ids.filter((option) => langs.indexOf(option.lang_id) >= 0));
        setValue("district_ids", watchedValues.district_ids.filter((option) => langs.indexOf(option.lang_id) >= 0));
        setValue("constituency_ids", watchedValues.constituency_ids.filter((option) => langs.indexOf(option.lang_id) >= 0));
      }
      
      return states; // Return states for chaining
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const state_idsOnchange = async (selected, langs, edit = false) => {
    let states = [];
    if (selected.length > 0) {
      if (typeof selected[0] === 'object') {
        // If selected is an array of objects, extract the state_id from each object
        states = selected.map(item => item.value.state_id);
      } else {
        // If selected is an array of integers, use them directly
        states = selected;
      }
    }
    
    try {
      const res = await api({
        url: '/location/getDistrictsByStateId',
        method: 'POST',
        responseType: 'json',
        data: { states, langs }
      });
      
      const districts = [];
      for await (let item of res.data) {
        const temp = { value: item, label: `${item.dist_name}` };
        districts.push(temp);
      }
      setDistrictOptions(districts);
      
      if (!edit && !isInitialLoad) {
        const temp = [];
        setValue("district_ids", watchedValues.district_ids.filter((option) => {
          if (states.indexOf(option.state_id) >= 0) {
            temp.push(option.dist_id);
            return true;
          }
        }));
        setValue("constituency_ids", watchedValues.constituency_ids.filter((option) => temp.indexOf(parseInt(option.dist_id)) >= 0));
      }
      
      return districts; // Return districts for chaining
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const district_idsOnchange = async (selected, langs, edit = false) => {
    let districts = [];
    if (selected.length > 0) {
      if (typeof selected[0] === 'object') {
        // If selected is an array of objects, extract the state_id from each object
        districts = selected.map(item => item.value.dist_id);
      } else {
        // If selected is an array of integers, use them directly
        districts = selected;
      }
    }
    
    try {
      const consties = await api({
        url: `/location/getConstituentsnew`,
        method: 'POST',
        responseType: 'json',
        data: { districts, langs }
      });
      
      const constituents = [];
      for await (let item of consties.data) {
        const temp = { value: item, label: `${item.const_name}` };
        constituents.push(temp);
      }
      setConstituencyOptions(constituents);
      
      if (!edit && !isInitialLoad) {
        setValue("constituency_ids", watchedValues.constituency_ids.filter((option) => districts.indexOf(parseInt(option.dist_id)) >= 0));
      }
      
      return constituents; // Return constituencies for chaining
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getSelectValues = (options, field, fieldValue) => {
    if (options.length > 0 && fieldValue && Array.isArray(fieldValue)) {
      switch (field) {
        case "lang_ids":
          // fieldValue is array of strings/numbers
          return options.filter((option) => fieldValue.some(val => val === option.value));
        case "state_ids":
          // fieldValue is array of objects with state_id
          if (fieldValue.length > 0 && typeof fieldValue[0] === 'object') {
            const stateIds = fieldValue.map((option) => option.state_id);
            return options.filter((option) => stateIds.indexOf(option.value?.state_id) >= 0);
          } else {
            // fieldValue is array of objects (direct values)
            return options.filter((option) => fieldValue.some(val => val === option.value?.state_id));
          }
        case "district_ids":
          // fieldValue is array of objects with dist_id
          if (fieldValue.length > 0 && typeof fieldValue[0] === 'object') {
            const districtIds = fieldValue.map((option) => option.dist_id);
            return options.filter((option) => districtIds.indexOf(option.value?.dist_id) >= 0);
          } else {
            return options.filter((option) => fieldValue.some(val => val === option.value?.dist_id));
          }
        case "constituency_ids":
          // fieldValue is array of objects with const_id
          if (fieldValue.length > 0 && typeof fieldValue[0] === 'object') {
            const constituencyIds = fieldValue.map((option) => option.const_id);
            return options.filter((option) => constituencyIds.indexOf(option.value?.const_id) >= 0);
          } else {
            return options.filter((option) => fieldValue.some(val => val === option.value?.const_id));
          }
        default:
          return [];
      }
    } else {
      return [];
    }
  };

  // Handle package details autofill - FIXED VERSION
  useEffect(() => {
    const loadPackageDetails = async () => {
      if (Object.keys(packageDetails).length > 0) {
        setIsLoading(true);
        setIsInitialLoad(true);
        
        try {
          // Set basic form values first
          setValue("package_type", packageDetails.picked_package ? packageDetails.picked_package : packageDetails.package_type);
          setValue("creative_type", packageDetails.creative_type);
          setValue("position", `${packageDetails.position}`);
          setValue("package_weeks", packageDetails.package_weeks);
          setValue("lang_ids", packageDetails.lang_ids);
          
          // Load states and wait for completion
          const loadedStates = await lang_idsOnchange(packageDetails.lang_ids, true);
          
          // After states are loaded, set state values
          if (loadedStates.length > 0) {
            const stateValues = getSelectValues(loadedStates, 'state_ids', packageDetails.state_ids);
            setValue("state_ids", stateValues.map(s => s.value));
            
            // Load districts and wait for completion
            const loadedDistricts = await state_idsOnchange(packageDetails.state_ids, packageDetails.lang_ids, true);
            
            // After districts are loaded, set district values
            if (loadedDistricts.length > 0) {
              const districtValues = getSelectValues(loadedDistricts, 'district_ids', packageDetails.district_ids);
              setValue("district_ids", districtValues.map(d => d.value));
              
              // Load constituencies and wait for completion
              const loadedConstituencies = await district_idsOnchange(packageDetails.district_ids, packageDetails.lang_ids, true);
              
              // After constituencies are loaded, set constituency values
              if (loadedConstituencies.length > 0) {
                const constituencyValues = getSelectValues(loadedConstituencies, 'constituency_ids', packageDetails.constituency_ids);
                setValue("constituency_ids", constituencyValues.map(c => c.value));
              }
              handleSubmit(onSubmit)();
            }
          }
          
          setIsInitialLoad(false);
          setIsLoading(false);
        } catch (error) {
          console.error("Error loading package details:", error);
          setIsInitialLoad(false);
          setIsLoading(false);
        }
      }
    };

    loadPackageDetails();
  }, [packageDetails]);
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h5>Package Type: <span className="capitalize">{watchedValues.package_type ==='Classified' ? 'Classified' : salesType}</span> </h5>
        <div>
          <label className="form-label">Price Package Type</label>
          <br />
          {salesType === "Alpha" && <><div className="form-check form-check-inline">
            <Controller
              name="package_type"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  className="form-check-input"
                  type="radio"
                  id="Alpha"
                  value="Alpha"
                  checked={field.value === "Alpha"}
                  disabled={disable}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('creative_type', '');
                    setPackageValue(0);
                    handleSubmit(onSubmit)();
                  }}
                />
              )}
            />
            <label className="form-check-label" htmlFor="Alpha">
              Alpha
            </label>
          </div>
          <div className="form-check form-check-inline">
            <Controller
              name="package_type"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  className="form-check-input"
                  type="radio"
                  id="Local"
                  value="Local"
                  checked={field.value === "Local"}
                  disabled={disable}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('creative_type', '');
                    setPackageValue(0);
                    handleSubmit(onSubmit)();
                  }}
                />
              )}
            />
            <label className="form-check-label" htmlFor="Local">
              Local
            </label>
          </div>
          </>}
          {salesType === "Inbound" && <div className="form-check form-check-inline">
            <Controller
              name="package_type"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  className="form-check-input"
                  type="radio"
                  id="Inbound"
                  value="Inbound"
                  checked={field.value === "Inbound"}
                  disabled={disable}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('creative_type', '');
                    setPackageValue(0);
                    handleSubmit(onSubmit)();
                  }}
                />
              )}
            />
            <label className="form-check-label" htmlFor="Inbound">
              Inbound
            </label>
          </div>}
          {salesType === "Local" &&<> <div className="form-check form-check-inline">
            <Controller
              name="package_type"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  className="form-check-input"
                  type="radio"
                  id="Local"
                  value="Local"
                  checked={field.value === "Local"}
                  disabled={disable}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('creative_type', '');
                    setPackageValue(0);
                    handleSubmit(onSubmit)();
                  }}
                />
              )}
            />
            <label className="form-check-label" htmlFor="Local">
              Local
            </label>
          </div>
          <div className="form-check form-check-inline">
          <Controller
            name="package_type"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                className="form-check-input"
                type="radio"
                id="Alpha"
                value="Alpha"
                checked={field.value === "Alpha"}
                disabled={disable}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  setValue('creative_type', '');
                  setPackageValue(0);
                  handleSubmit(onSubmit)();
                }}
              />
            )}
          />
          <label className="form-check-label" htmlFor="Alpha">
            Alpha
          </label>
        </div></>}
          <div className="form-check form-check-inline">
            <Controller
              name="package_type"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  className="form-check-input"
                  type="radio"
                  id="classified"
                  value="Classified"
                  checked={field.value === "Classified"}
                  disabled={disable}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('creative_type', '');
                    setPackageValue(0);
                    handleSubmit(onSubmit)();
                  }}
                />
              )}
            />
            <label className="form-check-label" htmlFor="classified">
              Classified
            </label>
          </div>
        </div>
        {errors.package_type && (
          <p className="text-red-600">{'Package type was required'}</p>
        )}

        <h4 className="text-center mt-2">Package Targets</h4>
        
        <div className="mb-3">
          <label htmlFor="" className="form-label">
            Language
          </label>
          <span className="text-red-600">*</span>
          <Controller
            name="lang_ids"
            control={control}
            render={({ field }) => (
              <Select
                aria-label="Default select example"
                placeholder="select language"
                value={getSelectValues(languageOptions, "lang_ids", field.value || [])}
                onChange={(selected) => {
                  const values = selected ? selected.map((item) => item.value) : [];
                  field.onChange(values);
                  if (!isInitialLoad) {
                    lang_idsOnchange(selected);
                    handleFormSubmissionWithReset('lang_ids', values);
                  }
                }}
                options={[...languageOptions.slice(0, 2),{label:'Oria',value:10}]}
                isMulti
                isDisabled={disable}
              />
            )}
          />
          {errors.lang_ids && (
            <div className="text-red-600">{'Language was required'}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="" className="form-label">
            State
          </label>
          <span className="text-red-600">*</span>
          <Controller
            name="state_ids"
            control={control}
            render={({ field }) => (
              <Select
                aria-label="Default select example"
                placeholder="Select States"
                value={getSelectValues(stateOptions, "state_ids", field.value || [])}
                onChange={(selected) => {
                  const values = selected ? selected.map((item) => item.value) : [];
                  field.onChange(values);
                  if (!isInitialLoad) {
                    state_idsOnchange(selected, watchedValues.lang_ids);
                    // Trigger form submission after state change
                    handleFormSubmissionWithReset('state_ids', values);
                  }
                }}
                options={stateOptions}
                isMulti
                isLoading={isLoading}
                isDisabled={disable}
              />
            )}
          />
          {errors.state_ids && (
            <div className="text-red-600">{'State was required'}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="" className="form-label">
            Districts
          </label>
          <Controller
            name="district_ids"
            control={control}
            render={({ field }) => (
              <Select
                aria-label="Default select example"
                placeholder="Select Districts"
                value={getSelectValues(districtOptions, "district_ids", field.value || [])}
                onChange={(selected) => {
                  const values = selected ? selected.map((item) => item.value) : [];
                  field.onChange(values);
                  if (!isInitialLoad) {
                    district_idsOnchange(selected, watchedValues.lang_ids);
                    // Trigger form submission after district change
                    handleFormSubmissionWithReset('district_ids', values);
                  }
                }}
                options={districtOptions}
                isMulti
                isLoading={isLoading}
                isDisabled={disable}
              />
            )}
          />
          {errors.district_ids && (
            <div className="text-red-600">{'District was required'}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="" className="form-label">
            Constituency
          </label>
          <Controller
            name="constituency_ids"
            control={control}
            render={({ field }) => (
              <Select
                aria-label="Default select example"
                placeholder="Select Constituency"
                value={getSelectValues(constituencyOptions, "constituency_ids", field.value || [])}
                onChange={(selected) => {
                  const values = selected ? selected.map((item) => item.value) : [];
                  field.onChange(values);
                  if (!isInitialLoad) {
                    // Trigger form submission after constituency change
                    handleFormSubmissionWithReset('constituency_ids', values);
                  }
                }}
                options={constituencyOptions}
                isMulti
                isLoading={isLoading}
                isDisabled={disable}
              />
            )}
          />
          {errors.constituency_ids && (
            <div className="text-red-600">{'Constituency was required'}</div>
          )}
        </div>

        {(['Alpha', 'Local', 'Inbound'].includes(watchedValues.package_type)) && <div className="mb-3">
          <label htmlFor="" className="form-label">
            Position
          </label>
          <span className="text-red-600">*</span>
          <Controller
            name="position"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className="form-select"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  if (!isInitialLoad) {
                      handleFormSubmissionWithReset('position', e.target.value);
                  }
                }}
                disabled={disable}
              >
                <option value="">Select Position</option>
                {positionOptions.map((position) => (
                  <option key={position.value} value={position.value}>
                    {position.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.position && (
            <div className="text-red-600">{'Position was required'}</div>
          )}
        </div>}

        <div className="mb-3">
          <label htmlFor="" className="form-label">
            Creative Type
          </label>
          <span className="text-red-600">*</span>
          <Controller
            name="creative_type"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className="form-select"
                aria-label="Default select example"
                onChange={(e) => {
                  field.onChange(e.target.value);
                  if (!isInitialLoad) {
                    handleFormSubmissionWithReset('creative_type', e.target.value);
                  }
                }}
                disabled={disable}
              >
                <option value="">select creative type</option>
                {watchedValues.package_type === "Classified" ? (
                  <option value="rectangle_classified">Rectangle Classified</option>
                ) : (
                  <>
                    <option value="Full Page">Full Image</option>
                    <option value="Full Page Video">Full Page Video</option>
                    <option value="Promoted Story">Promoted Story</option>
                  </>
                )}
              </select>
            )}
          />
          {errors.creative_type && (
            <div className="text-red-600">{'Creative type was required'}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="" className="form-label">
            Package Weeks
          </label>
          <Controller
            name="package_weeks"
            control={control}
            render={({ field }) => (
              <select {...field} className="form-select" onChange={(e) => {
                field.onChange(e.target.value);
                if (!isInitialLoad) {
                  handleFormSubmissionWithReset('package_weeks', e.target.value);
                }
              }}
              disabled={disable}
              >
                <option value="">Select Weeks</option>
               {watchedValues.package_type === 'Local' ? LocalPricePackages.map(item => (
                <option key={item.value} value={item.value}>{item.label}</option>
               )) :pricePackages.map(item => (
                <option key={item.value} value={item.value}>{item.label}</option>
               ))}
              </select>
            )}
          />
          {errors.package_weeks && (
            <div className="text-red-600">{'Package weeks was required'}</div>
          )}
        </div>

        <h6>Package Value: {packageValue}</h6>
        {isLoading && <div>Loading package details...</div>}
        <div className="mb-3 flex gap-3">
          <div>
            <label htmlFor="final package value" className="form-label">Final Pacakge Value</label>
            <input id="final pacakge value" type="number" className="form-control" defaultValuevalue={!isInitialLoad ? packageValue : updatedPackageValue} disabled={disable} value={updatedPackageValue} onChange={e => setUpdatedPackageValue(e.target.value)} placeholder="Enter Updated package Value" />
        </div>
          <div>
            <label className="form-label">percentage Change</label><br/>
            <p className="font-semibold">{Math.round(percentChange(packageValue, updatedPackageValue))}</p>
          </div>
        </div>
      </form>
    </>
  );
}

export default PackageOrder;